import { createChildLogger } from "@retreat/shared";
import { getClient } from "@retreats/supabase-adapter";
import { drainSyncQueue } from "./sessionSyncService";
import { syncUserSchedules } from "./userScheduleSyncService";
import { PushAlarmService } from "./PushAlarmService";
import { reportError } from "../utils/reportError";
import type { PreferredTradition } from "../state/settingsState";

function isKnownPreferredTradition(v: unknown): v is PreferredTradition | null {
  return v === "zen" || v === "vipassana" || v === null;
}
import { STORAGE_KEYS } from "../constants";
import type { UIAction } from "../bridge/types";
import type { Dispatch } from "react";

export interface SettingsWriter {
  setBellsEnabled(v: boolean): void;
  setTTSEnabled(v: boolean): void;
  setWhispersEnabled(v: boolean): void;
  setPreferredTradition(v: PreferredTradition | null): void;
  setAudioReflectionEnabled(v: boolean): void;
}

const log = createChildLogger("SyncManager");

const PULL_COOLDOWN_MS = 5 * 60 * 1000;

class SyncManager {
  private static _instance: SyncManager | null = null;
  private _draining = false;
  private _lastPullAt = 0;
  private _dispatch: Dispatch<UIAction> | null = null;
  private _settings: SettingsWriter | null = null;

  static get(): SyncManager {
    if (!SyncManager._instance) {
      SyncManager._instance = new SyncManager();
    }
    return SyncManager._instance;
  }

  setSyncDispatch(dispatch: Dispatch<UIAction>): void {
    this._dispatch = dispatch;
  }

  configureSettings(writer: SettingsWriter): void {
    this._settings = writer;
  }

  async drain(): Promise<void> {
    if (this._draining) {
      log.trace("drain: already in progress — skipping");
      return;
    }
    this._draining = true;
    try {
      await drainSyncQueue();
      // Retry any push alarm registration that failed while offline
      const confirmed = await PushAlarmService.flushPending();
      if (confirmed !== null) {
        this._dispatch?.({ type: "PUSH_ALARM_REGISTRATION_RESULT", confirmed });
      }
    } finally {
      this._draining = false;
    }
  }

  async pull(userId: string): Promise<void> {
    if (Date.now() - this._lastPullAt < PULL_COOLDOWN_MS) {
      log.trace("pull: within cooldown — skipping");
      return;
    }

    try {
      const client = getClient();

      // Fetch sessions
      const { data: remoteSessions } = await client
        .from("sessions")
        .select("id, retreat_id, retreat_name, started_at, ended_at, status, updated_at")
        .eq("user_id", userId)
        .order("started_at", { ascending: false })
        .limit(200);

      // Fetch containers — restored onto sessions that have no local container data
      const { data: remoteContainers } = await client
        .from("session_containers")
        .select("session_id, container_id, container_name, container_type, container_posture, scheduled_duration_seconds, actual_duration_seconds, completed_at, is_complete")
        .eq("user_id", userId)
        .order("completed_at", { ascending: true })
        .limit(2000);

      // Fetch reflections — restored when no local draft exists (local always wins on conflict)
      const { data: remoteReflections } = await client
        .from("reflections")
        .select("session_id, text")
        .eq("user_id", userId)
        .limit(500);

      // Fetch user_settings
      const { data: remoteSettings } = await client
        .from("user_settings")
        .select("bells_enabled, voice_enabled, whispers_enabled, preferred_tradition, audio_reflection_enabled, updated_at")
        .eq("user_id", userId)
        .maybeSingle();

      // Merge settings — remote wins if newer than last local write
      let settingsUpdated = false;
      if (remoteSettings) {
        settingsUpdated = this._mergeSettings(remoteSettings);
      }

      // Sessions: apply in-progress guard — never overwrite active sessions
      if (remoteSessions && remoteSessions.length > 0) {
        this._mergeSessions(remoteSessions);
      }

      // Containers: write only when no local container data exists for the session
      if (remoteContainers && remoteContainers.length > 0) {
        this._mergeContainers(remoteContainers);
      }

      // Reflections: write only when no local draft exists for the session (local wins)
      if (remoteReflections && remoteReflections.length > 0) {
        this._mergeReflections(remoteReflections);
      }

      if (settingsUpdated && this._dispatch) {
        this._dispatch({ type: "SETTINGS_HYDRATED" });
      }

      // Two-way sync for user-authored schedules (push local → remote, pull remote → local)
      await syncUserSchedules(client, userId);

      this._lastPullAt = Date.now();
      log.info("pull: complete", { userId });
    } catch (err) {
      reportError(err, "SYNC_PULL_FAILED");
      log.warn("pull: failed (%s) — swallowing", err instanceof Error ? err.constructor.name : String(err));
    }
  }

  init(): () => void {
    const handleOnline = () => {
      log.info("Network restored — draining sync queue");
      void this.drain();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void this.drain();
      }
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }

  // Exposed for testing
  _resetCooldownForTest(): void {
    this._lastPullAt = 0;
  }

  private _mergeSettings(remote: Record<string, unknown>): boolean {
    const localUpdatedAtRaw = (() => {
      try { return localStorage.getItem(STORAGE_KEYS.SETTINGS_UPDATED_AT); } catch { return null; }
    })();
    const localUpdatedAt = localUpdatedAtRaw ? Number(localUpdatedAtRaw) : 0;

    // remote.updated_at is an ISO string from Supabase
    const remoteUpdatedAt = remote.updated_at
      ? new Date(remote.updated_at as string).getTime()
      : 0;

    if (remoteUpdatedAt <= localUpdatedAt) return false;

    // Remote is newer — write to localStorage via injected writer
    if (this._settings) {
      if (typeof remote.bells_enabled === "boolean") this._settings.setBellsEnabled(remote.bells_enabled);
      if (typeof remote.voice_enabled === "boolean") this._settings.setTTSEnabled(remote.voice_enabled);
      if (typeof remote.whispers_enabled === "boolean") this._settings.setWhispersEnabled(remote.whispers_enabled);
      if (isKnownPreferredTradition(remote.preferred_tradition)) {
        this._settings.setPreferredTradition(remote.preferred_tradition);
      }
      if (typeof remote.audio_reflection_enabled === "boolean") this._settings.setAudioReflectionEnabled(remote.audio_reflection_enabled);
    }

    log.debug("pull: settings updated from remote", { remoteUpdatedAt, localUpdatedAt });
    return true;
  }

  private _mergeSessions(remoteSessions: Record<string, unknown>[]): void {
    for (const remote of remoteSessions) {
      try {
        const sessionId = remote["id"] as string;
        const sessionKey = STORAGE_KEYS.SESSION_PREFIX + sessionId;
        const localRaw = localStorage.getItem(sessionKey);
        if (localRaw) {
          const local = JSON.parse(localRaw);
          // Never overwrite an in-progress session with stale remote data
          if (local.status === "in_progress") continue;
        }
        // Only write if the record doesn't exist locally (pull doesn't overwrite known completed sessions)
        if (!localRaw) {
          localStorage.setItem(sessionKey, JSON.stringify({
            id: remote["id"],
            retreatId: remote["retreat_id"],
            retreatName: remote["retreat_name"],
            startedAt: remote["started_at"] ? new Date(remote["started_at"] as string).getTime() : 0,
            endedAt: remote["ended_at"] ? new Date(remote["ended_at"] as string).getTime() : null,
            status: remote["status"],
            containers: [],
          }));
        }
      } catch {
        /* skip corrupt entries */
      }
    }
  }

  private _mergeContainers(remoteContainers: Record<string, unknown>[]): void {
    const bySession = new Map<string, Record<string, unknown>[]>();
    for (const c of remoteContainers) {
      const sessionId = c["session_id"] as string;
      if (!bySession.has(sessionId)) bySession.set(sessionId, []);
      bySession.get(sessionId)!.push(c);
    }

    for (const [sessionId, containers] of bySession.entries()) {
      try {
        const key = STORAGE_KEYS.CONTAINERS_PREFIX + sessionId;
        if (localStorage.getItem(key)) continue; // local wins
        const mapped = containers.map((c) => ({
          containerId: c["container_id"],
          containerName: c["container_name"],
          containerType: c["container_type"],
          containerPosture: c["container_posture"] ?? undefined,
          scheduledDurationSeconds: Number(c["scheduled_duration_seconds"]),
          actualDurationSeconds: Number(c["actual_duration_seconds"]),
          completedAt: c["completed_at"]
            ? new Date(c["completed_at"] as string).getTime()
            : Date.now(),
          isComplete: Boolean(c["is_complete"]),
        }));
        localStorage.setItem(key, JSON.stringify(mapped));
        log.debug("pull: restored containers for session", { sessionId, count: mapped.length });
      } catch {
        /* skip corrupt entries or quota */
      }
    }
  }

  private _mergeReflections(remoteReflections: Record<string, unknown>[]): void {
    for (const r of remoteReflections) {
      try {
        const sessionId = r["session_id"] as string;
        const text = r["text"] as string | null;
        if (!sessionId || !text) continue;
        const draftKey = `${STORAGE_KEYS.REFLECTION_DRAFT}_${sessionId}`;
        if (localStorage.getItem(draftKey)) continue; // local wins
        localStorage.setItem(draftKey, text);
        log.debug("pull: restored reflection for session", { sessionId });
      } catch {
        /* skip corrupt entries or quota */
      }
    }
  }
}

export const syncManager = SyncManager.get();
